import { useAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconChevronDown,
  IconChevronUp,
  IconExpand,
  IconThumbtack,
  IconThumbtackSolid,
  IconXmark,
} from '@optiaxiom/icons';
import { Box, Button, Flex, Heading, theme, Tooltip } from '@optiaxiom/react';

import {
  stickyboardDrawerModeAtom,
  stickyboardDrawerVisibleAtom,
  stickyboardDrawerWidthAtom,
} from '@frontend/atoms/stickyboardAtoms';

import { hideScrollbar } from './Stickyboard.css';
import { StickyboardBoard } from './StickyboardBoard';

type Props = {
  /**
   * Geometry of the content stage (measured by BaseLayout). The drawer is a
   * fixed, high z-index overlay aligned to the content area's left edge — so it
   * sits above the chat MFE and chat overlay (which escape the stage's
   * stacking context and would otherwise clip a drawer rendered inside it).
   */
  stageLeft?: number;
  stageTop?: number;
  stageHeight?: number;
};

/**
 * Left-anchored Stickyboard drawer. Opened by a single click on the left-rail
 * icon (a double click opens the full page). Renders the same StickyboardBoard
 * as the full page — the board fires the auto-curate pass on mount.
 *
 * Two modes (toggled from the header, persisted in stickyboardDrawerModeAtom):
 *   - "peek" (float): overlays the content from the left; no backdrop. An
 *     outside click (or Escape) dismisses it, like a popover.
 *   - "held" (dock): the content area shifts right to fit (BaseLayout adds a
 *     left margin equal to the drawer width); it stays open until closed.
 */
export function StickyboardDrawer({ stageLeft = 0, stageTop = 0, stageHeight }: Props) {
  const [visible, setVisible] = useAtom(stickyboardDrawerVisibleAtom);
  const [width] = useAtom(stickyboardDrawerWidthAtom);
  const [mode, setMode] = useAtom(stickyboardDrawerModeAtom);
  const navigate = useNavigate();
  const { instanceId } = useParams();
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [canScrollUp, setCanScrollUp] = useState(false);

  const isPeek = mode === 'peek';

  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVisible(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, setVisible]);

  // Float (peek) mode dismisses on any outside interaction, like a popover.
  // Dock (held) mode stays put. The rail nav item is excluded so its own
  // toggle owns that click instead of double-firing.
  useEffect(() => {
    if (!visible || !isPeek) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!panelRef.current || panelRef.current.contains(target)) return;
      if (target?.closest?.('[data-testid="stickyboard-nav-item"]')) return;
      setVisible(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [visible, isPeek, setVisible]);

  // Track whether there's more board above/below the fold, so the up/down
  // arrow affordances show. Observes both the scroller (resize) and its content
  // (which grows as the board loads).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setCanScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
      setCanScrollUp(el.scrollTop > 8);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    if (contentRef.current) observer.observe(contentRef.current);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [visible]);

  if (!visible) return null;

  const openFullPage = () => {
    setVisible(false);
    navigate(`/instances/${instanceId}/stickyboard`);
  };

  const scrollDown = () =>
    scrollRef.current?.scrollBy({ top: scrollRef.current.clientHeight * 0.8, behavior: 'smooth' });

  const scrollUp = () =>
    scrollRef.current?.scrollBy({ top: -scrollRef.current.clientHeight * 0.8, behavior: 'smooth' });

  return (
    <Box
      ref={panelRef}
      role="dialog"
      aria-label="Stickyboard"
      data-testid="stickyboard-drawer"
      style={{
        position: 'fixed',
        left: stageLeft,
        top: stageTop,
        height: stageHeight ?? '100vh',
        width,
        maxWidth: '100%',
        background: theme.colors['bg.default'],
        borderRight: `1px solid ${theme.colors['border.tertiary']}`,
        // Float (peek) lifts above the content with a shadow; dock (held) sits
        // flush in the space the content vacated, so no shadow.
        boxShadow: isPeek ? '2px 0 16px rgba(0,0,0,0.12)' : 'none',
        // Above the chat overlay (1100) and the chat MFE's fixed elements.
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Flex
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        style={{ padding: 16, borderBottom: `1px solid ${theme.colors['border.tertiary']}` }}
      >
        <Heading level="3">Stickyboard</Heading>
        <Flex flexDirection="row" gap="4" alignItems="center">
          <Tooltip content={isPeek ? 'Dock' : 'Float'}>
            <Button
              size="sm"
              appearance="subtle"
              aria-label={isPeek ? 'Dock' : 'Float'}
              icon={isPeek ? <IconThumbtack /> : <IconThumbtackSolid />}
              onClick={() => setMode(isPeek ? 'held' : 'peek')}
            />
          </Tooltip>
          <Tooltip content="Open full page">
            <Button
              size="sm"
              appearance="subtle"
              aria-label="Open full page"
              icon={<IconExpand />}
              onClick={openFullPage}
            />
          </Tooltip>
          <Tooltip content="Close">
            <Button
              size="sm"
              appearance="subtle"
              aria-label="Close"
              icon={<IconXmark />}
              onClick={() => setVisible(false)}
            />
          </Tooltip>
        </Flex>
      </Flex>
      <Box style={{ position: 'relative', flex: 1, minHeight: 0 }}>
        <Box
          ref={scrollRef}
          className={hideScrollbar}
          style={{ height: '100%', overflow: 'auto', padding: 16 }}
        >
          <Box ref={contentRef}>
            <StickyboardBoard />
          </Box>
        </Box>
        {canScrollUp && (
          <button
            type="button"
            aria-label="Scroll up"
            onClick={scrollUp}
            style={{
              position: 'absolute',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 32,
              height: 32,
              padding: 0,
              borderRadius: 9999,
              border: `1px solid ${theme.colors['border.tertiary']}`,
              background: theme.colors['bg.default'],
              boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <IconChevronUp />
          </button>
        )}
        {canScrollDown && (
          <button
            type="button"
            aria-label="Scroll down"
            onClick={scrollDown}
            style={{
              position: 'absolute',
              bottom: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 32,
              height: 32,
              padding: 0,
              borderRadius: 9999,
              border: `1px solid ${theme.colors['border.tertiary']}`,
              background: theme.colors['bg.default'],
              boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <IconChevronDown />
          </button>
        )}
      </Box>
    </Box>
  );
}
